import {
  type WhitelistItems,
  whitelistItemsSchema,
} from "@gearbox-protocol/cli-utils";
import {
  Button,
  cn,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorBox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  NetworkSelect,
} from "@gearbox-protocol/deploy-tools-ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { api } from "api";
import MultiInput from "components/forms/MultiInput";
import type { FC, FormEvent } from "react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { preventSubmitOnEnter } from "utils";

interface WhitelistCreateDialogProps {
  category: string;
  toggleOpen: (open: boolean) => void;
}

const WhitelistCreateDialog: FC<WhitelistCreateDialogProps> = ({
  category,
  toggleOpen,
}) => {
  const [error, setError] = useState("");

  const form = useForm<WhitelistItems>({
    resolver: zodResolver(whitelistItemsSchema),
    defaultValues: {
      network: "Mainnet",
      reason: "",
    },
  });
  const { handleSubmit } = form;

  const submit = useCallback(
    (e: FormEvent) => {
      handleSubmit(async data => {
        try {
          await api.addToWhitelist(category, data);
          toggleOpen(false);
          window.location.reload();
        } catch (err) {
          setError(`${err}`);
          e.preventDefault();
        }
      })(e).catch(err => setError(`${err}`));
    },
    [category, handleSubmit, toggleOpen],
  );

  const expiresAt = form.watch("expiresAt");

  return (
    <Dialog open={true} onOpenChange={toggleOpen}>
      <DialogContent className="w-3xl">
        <DialogHeader>
          <DialogTitle>Add addresses to whitelist</DialogTitle>
          <DialogDescription>
            Multiple addresses can be added by separating them with commas. Can
            include credit accounts/credit managers/tokens
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={submit}
            onKeyDown={preventSubmitOnEnter}
            className="space-y-2"
          >
            <FormField
              control={form.control}
              name="network"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center">
                  <FormLabel className="w-28">Network</FormLabel>
                  <FormControl>
                    <NetworkSelect {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="addresses"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center">
                  <FormLabel className="w-28">Addresses</FormLabel>
                  <FormControl>
                    <MultiInput
                      field={field}
                      className="h-8"
                      placeholder="addresses"
                      separator={/[,\s]+/}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center">
                  <FormLabel className="w-28">Reason</FormLabel>
                  <FormControl>
                    <Input placeholder="reason" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-row items-center gap-2">
              <FormField
                control={form.control}
                name="expiresAt"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center">
                    <FormLabel className="w-23">Expires at</FormLabel>
                    <DatePicker
                      value={field.value ? new Date(field.value) : undefined}
                      onChange={d => field.onChange(d?.getTime())}
                      placeholder="never"
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                variant="link"
                type="button"
                onClick={e => {
                  form.setValue("expiresAt", undefined);
                  e.preventDefault();
                }}
                className={cn({ hidden: !expiresAt })}
              >
                Clear
              </Button>
            </div>
          </form>
        </Form>

        {!!error && <ErrorBox error={error} />}

        <DialogFooter>
          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            loading={form.formState.isSubmitting}
            onClick={submit}
          >
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WhitelistCreateDialog;
